import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Link, NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import { NavbarProps } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import UserAccount from './UserAccount';
import ThemeToggle from './ThemeToggle';
import cardReviveMark from '../Assets/logos/cardrevive-mark-onlight.svg';
import cardReviveWordmark from '../Assets/logos/cardrevive-wordmark-onlight.svg';
import cardhubAustralia from '../Assets/logos/cardhub-logo-Black-96.webp';
import {
  MOBILE_CALENDAR_CLOSE_EVENT,
  MOBILE_NAVIGATION_OPEN_EVENT,
  MOBILE_OVERLAYS_CLOSE_EVENT,
} from '../utils/mobileNavigation';

// This pill's background is a fixed #ffffff in both light and dark app
// themes (see SponsorLogo below), so the brand's cream (#fffcf4) mark/
// wordmark - designed for dark surfaces - would be near-invisible here.
// cardrevive-mark-onlight.svg / cardrevive-wordmark-onlight.svg are the same
// artwork with that fill swapped to the brand blue (#1B6FE8) instead.
const CARDREVIVE_NAVBAR_URL = 'https://www.cardrevive.com.au/?utm_source=tcgcalendar&utm_medium=referral&utm_campaign=navbar';

const Banner = styled.div`
  background-color: #111827;
  color: #f9fafb;
  text-align: center;
  padding: 0.45rem 1rem;
  font-size: 0.9rem;

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavbarStyled = styled.nav`
  position: sticky;
  top: 0;
  z-index: 1070;
  background-color: var(--surface);
  padding: 0.65rem 1rem;
  box-shadow: 0 0.125rem 0.25rem 0 var(--shadow);
  width: 100%;
  transition: background-color 0.3s ease;
  flex-shrink: 0; /* Prevent navbar from shrinking */
`;

const NavbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 88rem;
  margin: 0 auto;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
    gap: 1rem;
  }
`;

const NavbarBrand = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  z-index: 1001;
  overflow: hidden;
  border-radius: 8px;

  &:hover {
    text-decoration: none;
  }

  img {
    height: 3.4rem;
    width: auto;
    transform: scale(1.05);
    
    @media (max-width: 768px) {
      height: 2.4rem;
      max-width: 200px;
    }
    
    &:hover {
      filter: grayscale(40%);
      transform: scale(1.08);
      transition: transform 0.3s ease;
    }
  }
`;

const HamburgerButton = styled.button<{ $open: boolean }>`
  display: none;
  flex-direction: column;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  z-index: 1001;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    display: flex;
    margin-left: 0;
  }
  
  span {
    width: 25px;
    height: 3px;
    background-color: var(--text);
    margin: 3px 0;
    transition: 0.3s;
    
    &:nth-child(1) {
      transform: ${props => props.$open ? 'rotate(-45deg) translate(-5px, 6px)' : 'none'};
    }
    
    &:nth-child(2) {
      opacity: ${props => props.$open ? '0' : '1'};
    }
    
    &:nth-child(3) {
      transform: ${props => props.$open ? 'rotate(45deg) translate(-5px, -6px)' : 'none'};
    }
  }
`;

const MobileSearchButton = styled.button`
  display: none;

  @media (max-width: 768px) {
    display: inline-flex;
    width: 44px;
    height: 44px;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    padding: 0;
    border: 0;
    border-radius: 9px;
    background: transparent;
    color: var(--text);
    cursor: pointer;

    svg {
      width: 23px;
      height: 23px;
    }

    &:hover,
    &:focus-visible {
      background: color-mix(in srgb, var(--primary) 12%, transparent);
      color: var(--primary);
    }
  }
`;

const NavbarMenu = styled.div<{ $open: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  /* Mid-width desktop (769-1399px): the six game tabs move onto their own
     second row (see GameNavSection) so nothing ever clips. Width arithmetic
     includes Shows, Sets, Saved Events and both account states, so the game
     group stays on row two until the full 1440px layout has room. */
  @media (min-width: 769px) and (max-width: 1399px) {
    flex-wrap: wrap;
    justify-content: flex-end;
    row-gap: 0.15rem;
  }

  @media (max-width: 768px) {
    position: fixed;
    top: 0;
    right: ${props => props.$open ? '0' : '-100%'};
    width: 90%;
    max-width: 320px;
    min-height: 100vh;
    min-height: 100dvh;
    height: auto;
    background-color: var(--surface);
    flex-direction: column;
    justify-content: flex-start;
    padding: 5rem 1rem 2rem;
    padding-top: max(5rem, env(safe-area-inset-top));
    padding-bottom: max(2rem, env(safe-area-inset-bottom));
    gap: 2rem;
    transition: right 0.3s ease;
    box-shadow: -2px 0 5px var(--shadow);
    z-index: 1000;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    visibility: ${props => props.$open ? 'visible' : 'hidden'};
    pointer-events: ${props => props.$open ? 'auto' : 'none'};
  }
`;

const NavSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;

  @media (max-width: 768px) {
    width: 100%;
    padding: 0 2rem;
    text-align: center;
    flex-direction: column;
  }
`;

// The six game tabs share one section. On mid-width desktops (769-1399px) it
// takes its own full-width second row inside the wrapping NavbarMenu (order
// pushes it after the account actions visually; DOM order is unchanged for
// the mobile drawer, which stacks in source order).
const GameNavSection = styled(NavSection)`
  @media (min-width: 769px) and (max-width: 1399px) {
    order: 5;
    flex-basis: 100%;
    justify-content: center;
  }
`;

// Desktop shows the SHORT game label; the mobile drawer keeps the full name.
// Screen readers always get the full name via aria-label on the link.
const FullLabel = styled.span`
  @media (min-width: 769px) {
    display: none;
  }
`;

const ShortLabel = styled.span`
  display: none;

  @media (min-width: 769px) {
    display: inline;
  }
`;

const NavLink = styled(RouterNavLink)`
  color: var(--text);
  text-decoration: none;
  font-weight: 500;
  padding: 0.55rem 0.75rem;
  border-radius: 8px;
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover,
  &.active {
    color: var(--primary);
    background-color: color-mix(in srgb, var(--primary) 12%, transparent);
    text-decoration: none;
  }

  /* Tighter tabs on mid-width desktops so six game tabs fit; same pill
     visual language, just less horizontal padding. */
  @media (min-width: 769px) and (max-width: 1280px) {
    padding: 0.55rem 0.5rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    width: 100%;
    display: block;
  }
`;

const Overlay = styled.div<{ $open: boolean }>`
  display: none;
  
  @media (max-width: 768px) {
    display: ${props => props.$open ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

const AccountActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    flex-wrap: wrap;
  }
`;

const MobilePoweredBy = styled.div`
  display: none;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 0;
    border-top: 1px solid var(--border);
    margin-top: auto;
    width: 100%;
  }
`;

const PoweredByText = styled.span`
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-weight: 500;
`;

const SponsorLogos = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: center;
`;

const SponsorLogo = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.25rem 0.4rem;

  img {
    height: 1.75rem;
    width: auto;
    opacity: 0.8;
    transition: opacity 0.3s ease, transform 0.3s ease;
    
    &:hover {
      opacity: 1;
      transform: scale(1.05);
    }
  }
`;

const Navbar: React.FC<NavbarProps> = ({ lightLogo, darkLogo }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { isDark } = useTheme();
  const location = useLocation();

  const openMenu = useCallback(() => {
    // Release the month drawer's lock before this menu acquires its own.
    window.dispatchEvent(new window.Event(MOBILE_CALENDAR_CLOSE_EVENT));
    setIsOpen(true);
  }, []);
  const closeMenu = () => setIsOpen(false);
  const toggleMenu = () => {
    if (isOpen) closeMenu();
    else openMenu();
  };
  const toggleSearch = () => {
    closeMenu();
    window.dispatchEvent(new window.Event('tcg-calendar-toggle-search'));
  };

  // Select logo based on theme
  const currentLogo = isDark ? darkLogo : lightLogo;

  useEffect(() => {
    const closeOverlays = () => setIsOpen(false);
    window.addEventListener(MOBILE_NAVIGATION_OPEN_EVENT, openMenu);
    window.addEventListener(MOBILE_OVERLAYS_CLOSE_EVENT, closeOverlays);

    return () => {
      window.removeEventListener(MOBILE_NAVIGATION_OPEN_EVENT, openMenu);
      window.removeEventListener(MOBILE_OVERLAYS_CLOSE_EVENT, closeOverlays);
    };
  }, [openMenu]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const firstFocusable = menuRef.current?.querySelector<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Browser Back/Forward and any programmatic navigate() never fire a link
  // click, overlay click or Escape, so without this the drawer (and the body
  // scroll lock above) would survive onto whatever page the user landed on.
  // The overflow-restoration effect above cleans up correctly on its own: it
  // is keyed on isOpen, so setting isOpen false here still runs its cleanup
  // before this effect's own body (which then finds isOpen false and does
  // nothing further).
  useEffect(() => {
    setIsOpen(false);

    // A focused link inside the now-hidden drawer would leave focus stuck on
    // an invisible element; move it back to the document instead.
    if (menuRef.current?.contains(document.activeElement)) {
      (document.activeElement as HTMLElement | null)?.blur();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  return (
    <>
      <Banner>
        Your ultimate source for <strong>TCG events</strong> and <strong>tournaments</strong> across Australia
      </Banner>
      <NavbarStyled role="navigation" aria-label="Main navigation">
        <NavbarContainer>
          <NavbarBrand to="/" aria-label="Home" onClick={closeMenu}>
            <img
              src={currentLogo}
              alt="TCG Calendar Logo"
              width={128}
              height={isDark ? 61 : 54}
              decoding="async"
            />
          </NavbarBrand>

          <MobileSearchButton type="button" aria-label="Search events" onClick={toggleSearch}>
            <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="10.5" cy="10.5" r="6.5" />
              <path d="m15.5 15.5 5 5" />
            </svg>
          </MobileSearchButton>
          
          <HamburgerButton
            $open={isOpen}
            onClick={toggleMenu}
            aria-label="Toggle navigation menu"
            aria-expanded={isOpen}
            aria-controls="mobile-nav-menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </HamburgerButton>
          
          <Overlay $open={isOpen} onClick={closeMenu} aria-hidden="true" />
          
          <NavbarMenu id="mobile-nav-menu" ref={menuRef} $open={isOpen}>
            <NavSection>
              <NavLink to="/" end onClick={closeMenu}>
                Shows
              </NavLink>
              <NavLink to="/sets" onClick={closeMenu}>
                Sets
              </NavLink>
            </NavSection>
            
            {/* Six game tabs grouped into a single NavSection (the existing
                grouping primitive): a tight inline cluster on wide desktops,
                a full-width second row at 769-1399px, a stacked column in the
                mobile drawer. Desktop shows short labels; the drawer and
                screen readers keep the full names. */}
            <GameNavSection>
              <NavLink to="/pokemon-competitions" onClick={closeMenu} aria-label="Pokemon TCG Competitions">
                <FullLabel>Pokemon Competitions</FullLabel>
                <ShortLabel>Pokemon</ShortLabel>
              </NavLink>
              <NavLink to="/onepiece-competitions" onClick={closeMenu} aria-label="One Piece Card Game Tournaments">
                <FullLabel>One Piece Tournaments</FullLabel>
                <ShortLabel>One Piece</ShortLabel>
              </NavLink>
              <NavLink to="/magic-competitions" onClick={closeMenu} aria-label="Magic: The Gathering Tournaments">
                <FullLabel>MTG Tournaments</FullLabel>
                <ShortLabel>MTG</ShortLabel>
              </NavLink>
              <NavLink to="/riftbound-competitions" onClick={closeMenu} aria-label="Riftbound Events">
                <FullLabel>Riftbound Events</FullLabel>
                <ShortLabel>Riftbound</ShortLabel>
              </NavLink>
              <NavLink to="/lorcana-competitions" onClick={closeMenu} aria-label="Disney Lorcana Events">
                <FullLabel>Disney Lorcana Events</FullLabel>
                <ShortLabel>Lorcana</ShortLabel>
              </NavLink>
              <NavLink to="/fleshandblood-competitions" onClick={closeMenu} aria-label="Flesh and Blood Events">
                <FullLabel>Flesh and Blood Events</FullLabel>
                <ShortLabel>FaB</ShortLabel>
              </NavLink>
            </GameNavSection>

            <AccountActions>
              <ThemeToggle />
              <UserAccount />
            </AccountActions>
            
            <MobilePoweredBy>
              <PoweredByText>Powered by</PoweredByText>
              <SponsorLogos>
                <SponsorLogo
                  href={CARDREVIVE_NAVBAR_URL}
                  target="_blank"
                  rel="sponsored noopener noreferrer"
                  aria-label="Visit CardRevive"
                  onClick={closeMenu}
                >
                  <img src={cardReviveMark} alt="" aria-hidden="true" loading="lazy" width={525} height={525} />
                  <img src={cardReviveWordmark} alt="CardRevive" loading="lazy" width={700} height={360} />
                </SponsorLogo>
                <SponsorLogo 
                  href="https://linktr.ee/thecardhubaustralia" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-label="Visit The Card Hub Australia"
                  onClick={closeMenu}
                >
                  <img src={cardhubAustralia} alt="The Cardhub Australia" loading="lazy" width={480} height={262} />
                </SponsorLogo>
              </SponsorLogos>
            </MobilePoweredBy>
          </NavbarMenu>
        </NavbarContainer>
      </NavbarStyled>
    </>
  );
};

export default Navbar;
